import { InspectorControls, PanelColorSettings } from "@wordpress/block-editor";

import {
    PanelBody,
    ToggleControl,
    __experimentalView as View,
} from "@wordpress/components";

import UnitRange from "./components/UnitRange";
import BorderRange from "./components/BorderRange";

const { addFilter } = wp.hooks;

const buttonHoverExtension = (settings, name) => {
    if (name !== "core/button") {
        return settings;
    }
    // Zachowaj istniejące atrybuty
    const existingAttributes = settings.attributes || {};

    return {
        ...settings,
        attributes: {
            ...existingAttributes,
            enableHoverEffects: {
                type: "boolean",
                default: false,
            },
            hoverBackgroundColor: {
                type: "string",
                default: "",
            },
            hoverTextColor: {
                type: "string",
                default: "",
            },
            hoverBorderColor: {
                type: "string",
                default: "",
            },
            hoverBorderWidth: {
                type: "number",
                default: 1,
            },
            hoverBorderStyle: {
                type: "string",
                default: "solid",
            },
            hoverBorderRadius: {
                type: "number",
                default: 0,
            },
            hoverBorderRadiusUnit: {
                type: "string",
                default: "px",
            },
        },
    };
};

const withButtonHoverControls = wp.compose.createHigherOrderComponent(
    (BlockEdit) => {
        return (props) => {
            if (props.name !== "core/button") {
                return <BlockEdit {...props} />;
            }

            const { attributes, setAttributes, clientId } = props;

            const buttonClassName = `block-${clientId}`;

            // Dodajemy klasę hover-effects-enabled do istniejących klas
            const combinedClassName = `${buttonClassName}${
                attributes.enableHoverEffects ? " hover-effects-enabled" : ""
            }`;

            const editorStyles = `
                #${buttonClassName}{
                    --hover-background-color: ${
                        attributes.hoverBackgroundColor || "inherit"
                    };
                    --hover-text-color: ${
                        attributes.hoverTextColor || "inherit"
                    };
                    --hover-border-color: ${
                        attributes.hoverBorderColor || "inherit"
                    };
                    --hover-border-width: ${attributes.hoverBorderWidth}px;
                    --hover-border-style: ${attributes.hoverBorderStyle};
                    --hover-border-radius: ${attributes.hoverBorderRadius}${
                        attributes.hoverBorderRadiusUnit
                    };
                }
                #${buttonClassName} .hover-effects-enabled:hover {
                    background-color: var(--hover-background-color) !important;
                    color: var(--hover-text-color) !important;
                    border-color: var(--hover-border-color) !important;
                    border-width: var(--hover-border-width) !important;
                    border-style: var(--hover-border-style) !important;
                    border-radius: var(--hover-border-radius) !important;
                }
            `;

            const modifiedProps = {
                ...props,
                className: props.className
                    ? `${props.className} ${combinedClassName}`
                    : combinedClassName,
            };

            return (
                <>
                    <style>{editorStyles}</style>

                    <BlockEdit {...modifiedProps} />
                    <InspectorControls group="styles">
                        <PanelBody title="Hover" initialOpen={true}>
                            <View style={{ padding: "5px 0 15px 0" }}>
                                <ToggleControl
                                    label="Włącz efekty hover"
                                    checked={attributes.enableHoverEffects}
                                    onChange={(value) =>
                                        setAttributes({
                                            enableHoverEffects: value,
                                        })
                                    }
                                    __nextHasNoMarginBottom={true}
                                />
                            </View>

                            {attributes.enableHoverEffects && (
                                <>
                                    <View style={{ padding: "5px 0 15px 0" }}>
                                        <PanelColorSettings
                                            title="Color"
                                            initialOpen={true}
                                            enableAlpha={true}
                                            colorSettings={[
                                                {
                                                    value: attributes.hoverTextColor,
                                                    onChange: (color) =>
                                                        setAttributes({
                                                            hoverTextColor:
                                                                color,
                                                        }),
                                                    label: "Text",
                                                },
                                                {
                                                    value: attributes.hoverBackgroundColor,
                                                    onChange: (color) =>
                                                        setAttributes({
                                                            hoverBackgroundColor:
                                                                color,
                                                        }),
                                                    label: "Background",
                                                },
                                            ]}
                                        />
                                    </View>
                                    <View style={{ padding: "10px 0" }}>
                                        <BorderRange
                                            borderValue={{
                                                color: attributes.hoverBorderColor,
                                                style: attributes.hoverBorderStyle,
                                                width: `${attributes.hoverBorderWidth}`,
                                            }}
                                            onBorderChange={(newBorder) => {
                                                setAttributes({
                                                    hoverBorderColor:
                                                        newBorder.color,
                                                    hoverBorderStyle:
                                                        newBorder.style,
                                                    hoverBorderWidth:
                                                        newBorder.width,
                                                });
                                            }}
                                            rangeValue={
                                                attributes.hoverBorderWidth
                                            }
                                            onRangeChange={(value) =>
                                                setAttributes({
                                                    hoverBorderWidth: value,
                                                })
                                            }
                                            label="Border"
                                        />
                                    </View>
                                    <View style={{ padding: "10px 0" }}>
                                        <UnitRange
                                            rangeValue={
                                                attributes.hoverBorderRadius
                                            }
                                            onRangeChange={(value) =>
                                                setAttributes({
                                                    hoverBorderRadius: value,
                                                })
                                            }
                                            unitValue={{
                                                value: attributes.hoverBorderRadius,
                                                unit: attributes.hoverBorderRadiusUnit,
                                            }}
                                            onUnitChange={(newValue) => {
                                                setAttributes({
                                                    hoverBorderRadius:
                                                        newValue.value,
                                                    hoverBorderRadiusUnit:
                                                        newValue.unit,
                                                });
                                            }}
                                            label="Zaokrąglenie"
                                            min={0}
                                            max={50}
                                            __nextHasNoMarginBottom={true}
                                        />
                                    </View>
                                </>
                            )}
                        </PanelBody>
                    </InspectorControls>
                </>
            );
        };
    },
    "withButtonHoverControls"
);

const addButtonHoverProps = (extraProps, blockType, attributes) => {
    if (blockType.name !== "core/button") {
        return extraProps;
    }

    const buttonStyle = {
        ...(attributes.hoverBackgroundColor && {
            "--hover-background-color": attributes.hoverBackgroundColor,
        }),
        ...(attributes.hoverTextColor && {
            "--hover-text-color": attributes.hoverTextColor,
        }),
        ...(attributes.hoverBorderColor && {
            "--hover-border-color": attributes.hoverBorderColor,
        }),
        ...(attributes.hoverBorderWidth !== undefined && {
            "--hover-border-width": `${attributes.hoverBorderWidth}px`,
        }),
        ...(attributes.hoverBorderStyle && {
            "--hover-border-style": attributes.hoverBorderStyle,
        }),
        ...(attributes.hoverBorderRadius !== undefined && {
            "--hover-border-radius": `${attributes.hoverBorderRadius}${attributes.hoverBorderRadiusUnit}`,
        }),
    };

    return {
        ...extraProps,
        style: {
            ...extraProps.style,
            ...buttonStyle,
        },
        className: extraProps.className
            ? `${extraProps.className} ${
                  attributes.enableHoverEffects ? "hover-effects-enabled" : ""
              }`
            : attributes.enableHoverEffects
            ? "hover-effects-enabled"
            : "",
    };
};

addFilter(
    "blocks.registerBlockType",
    "danone/button-hover-attributes",
    buttonHoverExtension
);

addFilter(
    "editor.BlockEdit",
    "danone/with-button-hover-controls",
    withButtonHoverControls
);

addFilter(
    "blocks.getSaveContent.extraProps",
    "danone/button-hover-props",
    addButtonHoverProps
);
